import type { PatientResponse } from '@vagusx/clients';
import { AsyncCombobox } from '@/components/autocomplete/async-combobox';
import { getPatientClient } from '@/lib/api-clients';
import { formatDate } from '@/lib/format';

interface PatientComboboxProps {
  value: string | null;
  onValueChange: (id: string | null, label: string) => void;
  /** The chosen patient's name, when `value` is set before any search has run. */
  initialLabel?: string;
  hasError?: boolean;
  disabled?: boolean;
  /** Keep typed text that matches no patient (reported with a null id). */
  allowFreeText?: boolean;
}

export async function searchPatients(
  query: string,
  signal: AbortSignal
): Promise<PatientResponse[]> {
  const result = await getPatientClient().search({
    // `_search` is the field PatientService.search actually reads; it maps to the
    // repository's freeform search over name/phone/MRN/DOB. An unrecognised
    // field is NOT an error server-side — it simply leaves searchTerm
    // undefined, so the query is dropped and the first `limit` patients come
    // back unfiltered. This sent `name` and so showed ten arbitrary patients
    // whatever you typed, which made the booking dialog unusable for any org
    // with more than ten of them.
    queries: [{ field: '_search', operator: 'like', value: query }],
    limit: 10,
  });
  if (signal.aborted) return [];
  return result.items;
}

/**
 * Pick one existing patient: type to search, or open it to browse. The picker
 * the booking dialog, the waitlist and a document's patient share, so a patient
 * is found the same way in each.
 */
export function PatientCombobox({
  value,
  onValueChange,
  initialLabel,
  hasError,
  disabled,
  allowFreeText = false,
}: PatientComboboxProps): React.ReactElement {
  return (
    <AsyncCombobox<PatientResponse>
      value={value}
      onValueChange={onValueChange}
      searchFn={searchPatients}
      getItemId={(p) => p.id}
      getItemLabel={(p) => `${p.firstName} ${p.lastName}`}
      renderItem={(p) => (
        <div>
          <div className="font-medium">
            {p.firstName} {p.lastName}
          </div>
          <div className="text-xs text-muted-foreground">
            {p.phone ?? ''} {p.dateOfBirth ? `DOB: ${formatDate(p.dateOfBirth)}` : ''}
          </div>
        </div>
      )}
      placeholder="Search patients..."
      allowFreeText={allowFreeText}
      initialLabel={initialLabel || undefined}
      hasError={hasError}
      disabled={disabled}
    />
  );
}
